import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save,
  Send,
  DollarSign,
  AlertCircle,
  Calendar,
  FileText,
  Briefcase,
  RefreshCw,
  Clock,
  Paperclip,
  X,
  CheckCircle2,
} from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import SourcingRfpService from '../../services/sourcingRFPService';

// ─── Field wrapper ─────────────────────────────────────────────────────────

const Field = ({ label, icon, required, children }) => (
  <div>
    <label style={{
      display: 'flex', alignItems: 'center', gap: 6,
      fontSize: 11, fontWeight: 700, color: '#6b7280',
      textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
    }}>
      {icon && <span style={{ color: '#9ca3af' }}>{icon}</span>}
      {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
    </label>
    {children}
  </div>
);

const inputStyle = {
  width: '100%', padding: '9px 12px',
  border: '1px solid #d1d5db', borderRadius: 8,
  fontSize: 14, color: '#111827', background: '#fff',
  outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s ease',
  fontFamily: 'inherit',
};

const textareaStyle = {
  ...inputStyle,
  resize: 'vertical',
  minHeight: 90,
  lineHeight: 1.5,
};

// ─── Main Component ────────────────────────────────────────────────────────

const EditResponse = () => {
  const { id } = useParams();            // response ID
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rfp, setRfp]             = useState(null);
  const [attachedFiles, setAttachedFiles] = useState([]);

  const [formData, setFormData] = useState({
    proposalDescription: '',
    technicalDetails: '',
    estimatedTimeline: '',
    notes: '',
    lineItems: [],
  });

  // ── Load existing response ─────────────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        // Fetch all responses, find by ID
        const res = await SourcingRfpService.getResponses('');
        const arr = res.data?.data || res.data || [];
        const found = arr.find(r => r._id === id);

        if (!found) {
          showError('Response not found.');
          return;
        }

        if (found.status !== 'DRAFT') {
          showError('Only draft responses can be edited.');
          navigate('/sourcing/responses');
          return;
        }

        // Load the associated RFP for context
        const rfpIdVal = found.rfpId?._id || found.rfpId;
        if (rfpIdVal) {
          try {
            const rfpRes = await SourcingRfpService.getRfpById(rfpIdVal);
            const rfpData = rfpRes.data?.data || rfpRes.data || rfpRes;
            setRfp(rfpData);

            // Re-map line items using RFP data
            const rfpLineItems = rfpData.lineItems || [];
            const mergedLines = rfpLineItems.map(item => {
              const existing = found.lineItems?.find(
                li => (li.lineItemId?._id || li.lineItemId) === item._id
              );
              return {
                lineItemId: item._id,
                description: item.description || item.itemDescription,
                requirement: item.requirement,
                qty: item.qty || 1,
                price: existing?.price || 0,
                notes: existing?.notes || '',
              };
            });
            setFormData({
              proposalDescription: found.proposalDescription || '',
              technicalDetails: found.technicalDetails || '',
              estimatedTimeline: found.estimatedTimeline || '',
              notes: found.notes || '',
              lineItems: mergedLines,
            });
          } catch {
            // Fallback: use what's in the response
            setFormData({
              proposalDescription: found.proposalDescription || '',
              technicalDetails: found.technicalDetails || '',
              estimatedTimeline: found.estimatedTimeline || '',
              notes: found.notes || '',
              lineItems: (found.lineItems || []).map(li => ({
                lineItemId: li.lineItemId,
                description: li.description,
                requirement: li.requirement,
                qty: li.qty || 1,
                price: li.price || 0,
                notes: li.notes || '',
              })),
            });
          }
        }
      } catch (err) {
        console.error(err);
        showError('Failed to load response. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handlePriceChange = (index, value) => {
    const lines = [...formData.lineItems];
    lines[index].price = parseFloat(value) || 0;
    setFormData(f => ({ ...f, lineItems: lines }));
  };

  const handleLineNoteChange = (index, value) => {
    const lines = [...formData.lineItems];
    lines[index].notes = value;
    setFormData(f => ({ ...f, lineItems: lines }));
  };

  const calculateTotal = () =>
    formData.lineItems.reduce((s, i) => s + (i.price || 0), 0);

  const handleFileAdd = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachedFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const handleFileRemove = (idx) =>
    setAttachedFiles(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (status = 'SUBMITTED') => {
    const rfpIdVal = rfp?._id;
    if (!rfpIdVal) {
      showError('RFP context missing. Cannot save.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        lineItems: formData.lineItems,
        notes: formData.notes,
        proposalDescription: formData.proposalDescription,
        technicalDetails: formData.technicalDetails,
        estimatedTimeline: formData.estimatedTimeline,
        attachments: [],
        status,
      };
      await SourcingRfpService.submitResponse(rfpIdVal, payload);
      success(
        status === 'SUBMITTED'
          ? 'Proposal updated and submitted!'
          : 'Draft saved successfully.'
      );
      setTimeout(() => navigate('/sourcing/responses'), 1000);
    } catch (err) {
      showError(err.message || 'Failed to update response.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex flex-col justify-center items-center h-screen gap-3 text-gray-500">
      <RefreshCw className="animate-spin" size={32} style={{ color: 'var(--gold-primary, #b78a00)' }} />
      <p className="font-medium">Loading your response…</p>
    </div>
  );

  const totalBid = calculateTotal();

  return (
    <>
      <PageBreadcrumb />

      <PageHeader
        title="Edit Draft Response"
        description={`Responding to: ${rfp?.title || 'RFP'}`}
        onBack={() => navigate(-1)}
        actions={[
          {
            label: 'Save Draft',
            icon: <Save size={18} />,
            variant: 'outline',
            onClick: () => handleSubmit('DRAFT'),
            disabled: submitting,
          },
          {
            label: submitting ? 'Submitting…' : 'Submit Proposal',
            icon: submitting
              ? <RefreshCw className="animate-spin" size={18} />
              : <Send size={18} />,
            variant: 'primary',
            onClick: () => handleSubmit('SUBMITTED'),
            disabled: submitting,
          },
        ]}
      />

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 24px 40px' }}>

        {/* ── RFP Context Card ── */}
        {rfp && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-3">
                <Field label="RFP Title" icon={<Briefcase size={12} />}>
                  <div className="text-base font-semibold text-gray-900 bg-gray-50 p-3 rounded-lg border border-gray-100">
                    {rfp.title || 'Untitled RFP'}
                  </div>
                </Field>
                <Field label="Scope of Work" icon={<FileText size={12} />}>
                  <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100 leading-relaxed min-h-[60px]">
                    {rfp.scopeOfWork || rfp.scope || 'No scope provided.'}
                  </div>
                </Field>
              </div>
              <div className="space-y-3">
                <Field label="Response Deadline" icon={<Calendar size={12} />}>
                  <div className="text-sm font-medium text-red-600 bg-red-50 p-3 rounded-lg border border-red-100">
                    {rfp.responseDeadline
                      ? new Date(rfp.responseDeadline).toLocaleDateString(undefined, {
                          weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
                        })
                      : 'No Deadline Set'}
                  </div>
                </Field>
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-100 text-sm text-amber-800">
                  <p className="font-semibold mb-1 flex items-center gap-1">
                    <AlertCircle size={13} /> Edit Mode
                  </p>
                  <p className="opacity-80 text-xs">
                    You are editing a draft. Update your pricing and details before submitting.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Proposal Fields ── */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6 space-y-5">
          <h3 className="font-semibold text-gray-800 text-base border-b border-gray-100 pb-3">
            Proposal Details
          </h3>

          <Field label="Proposal Description" icon={<FileText size={12} />} required>
            <textarea
              style={textareaStyle}
              placeholder="Describe your overall proposal, approach, and value proposition…"
              value={formData.proposalDescription}
              onChange={e => setFormData(f => ({ ...f, proposalDescription: e.target.value }))}
              onFocus={e => e.target.style.borderColor = '#3b82f6'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
          </Field>

          <Field label="Technical Details" icon={<CheckCircle2 size={12} />}>
            <textarea
              style={{ ...textareaStyle, minHeight: 110 }}
              placeholder="Describe your technical approach, methodology, certifications, and compliance…"
              value={formData.technicalDetails}
              onChange={e => setFormData(f => ({ ...f, technicalDetails: e.target.value }))}
              onFocus={e => e.target.style.borderColor = '#3b82f6'}
              onBlur={e => e.target.style.borderColor = '#d1d5db'}
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Estimated Timeline" icon={<Clock size={12} />}>
              <input
                type="text"
                style={inputStyle}
                placeholder="e.g., 6 months, Q3 2025 delivery…"
                value={formData.estimatedTimeline}
                onChange={e => setFormData(f => ({ ...f, estimatedTimeline: e.target.value }))}
                onFocus={e => e.target.style.borderColor = '#3b82f6'}
                onBlur={e => e.target.style.borderColor = '#d1d5db'}
              />
            </Field>
            <Field label="Cover Note / Additional Remarks" icon={<FileText size={12} />}>
              <textarea
                style={{ ...textareaStyle, minHeight: 72 }}
                placeholder="Any additional notes, disclaimers, or comments…"
                value={formData.notes}
                onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))}
                onFocus={e => e.target.style.borderColor = '#3b82f6'}
                onBlur={e => e.target.style.borderColor = '#d1d5db'}
              />
            </Field>
          </div>
        </div>

        {/* ── Line Items Table ── */}
        {formData.lineItems.length > 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Bid Line Items</h3>
              <span className="text-xs bg-gray-200 px-2 py-1 rounded text-gray-600">
                {formData.lineItems.length} Items
              </span>
            </div>
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase w-1/3">Item / Requirement</th>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase w-44">Unit Price</th>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {formData.lineItems.map((item, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="p-4 align-top">
                      <p className="font-medium text-gray-900">{item.description}</p>
                      {item.qty && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded font-semibold">
                          Qty: {item.qty}
                        </span>
                      )}
                      {item.requirement && (
                        <p className="text-xs text-gray-400 mt-1">{item.requirement}</p>
                      )}
                    </td>
                    <td className="p-4 align-top">
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                        <input
                          type="number" min="0"
                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
                          placeholder="0.00"
                          value={item.price}
                          onChange={e => handlePriceChange(idx, e.target.value)}
                        />
                      </div>
                    </td>
                    <td className="p-4 align-top">
                      <textarea
                        rows="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
                        placeholder="Add comments…"
                        value={item.notes}
                        onChange={e => handleLineNoteChange(idx, e.target.value)}
                        onFocus={e => e.target.rows = 3}
                        onBlur={e => e.target.rows = 1}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-blue-50/50 border-t-2 border-blue-200">
                <tr>
                  <td className="p-4 text-sm text-gray-500">Auto-calculated total</td>
                  <td className="p-4 text-right font-bold text-gray-700">Total Bid:</td>
                  <td className="p-4">
                    <span className="text-xl font-bold text-blue-700">
                      ${totalBid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 p-8 rounded-xl text-center mb-6">
            <AlertCircle className="mx-auto text-yellow-500 mb-2" />
            <p className="text-yellow-700 font-medium">No line items found for this RFP.</p>
          </div>
        )}

        {/* ── Attachments ── */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Paperclip size={16} className="text-gray-500" />
            <h3 className="font-semibold text-gray-800 text-sm">
              Attachments <span className="text-gray-400 font-normal">(optional)</span>
            </h3>
          </div>

          {attachedFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {attachedFiles.map((f, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', background: '#f3f4f6',
                  borderRadius: 8, fontSize: 13, color: '#374151',
                }}>
                  <FileText size={13} className="text-gray-400" />
                  {f.name}
                  <button
                    onClick={() => handleFileRemove(i)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <X size={13} className="text-gray-400 hover:text-red-500" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 16px', border: '2px dashed #d1d5db',
            borderRadius: 8, cursor: 'pointer', color: '#6b7280',
            fontSize: 13, fontWeight: 500,
            transition: 'border-color 0.15s ease, color 0.15s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#2563eb'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#6b7280'; }}
          >
            <Paperclip size={15} />
            Add Documents
            <input
              type="file" multiple style={{ display: 'none' }}
              onChange={handleFileAdd}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.png,.jpg"
            />
          </label>
          <p className="text-xs text-gray-400 mt-2">
            Accepted: PDF, Word, Excel, PowerPoint, ZIP, Images (max 10MB each)
          </p>
        </div>

        {/* ── Action Buttons ── */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => navigate(-1)}
            style={{
              padding: '10px 20px', border: '1px solid #d1d5db',
              borderRadius: 8, background: '#fff', color: '#374151',
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => handleSubmit('DRAFT')}
            disabled={submitting}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '10px 20px', border: '1px solid #d1d5db',
              borderRadius: 8, background: '#fff', color: '#374151',
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
              opacity: submitting ? 0.6 : 1,
            }}
          >
            <Save size={16} /> Save Draft
          </button>
          <button
            onClick={() => handleSubmit('SUBMITTED')}
            disabled={submitting}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '10px 20px', border: 'none',
              borderRadius: 8,
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(37,99,235,0.35)',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting
              ? <><RefreshCw className="animate-spin" size={16} /> Submitting…</>
              : <><Send size={16} /> Submit Proposal</>
            }
          </button>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default EditResponse;
